<template>
    <div class="side-nav-popup">
        <div :class="{ showBack }" class="side-nav-inner">
            <slot/>

            <div v-show="confirm.opened" class="confirm">
                <div class="container">
                    <slot :accept="acceptConfirm" :cancel="cancelConfirm" name="confirm">
                        <div class="title">{{ confirm.title }}</div>
                        <div v-show="confirm.text" class="message">{{ confirm.text }}</div>
                        <div class="buttons">
                            <input :value="confirm.cancel" class="ghost" type="button" @click="cancelConfirm" />
                            <input :value="confirm.accept" class="primary" type="button" @click="acceptConfirm" />
                        </div>
                    </slot>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getActual } from '@/common/session.js';
import { StackContainer } from '@dsee/components/popups/popup.container.js';
import { Popup } from '@dsee/components/popups/popup.js';
import { newPromiseProxy } from '@dsee/util/async.js';

const container = new StackContainer({ zIndex: 500 });


function emptyConfirm() {
    return {
        title: 'Изменения не сохранятся',
        text: 'На странице есть несохраненные изменения, которые будут потеряны',
        accept: 'Закрыть без сохранения',
        cancel: 'Отмена',
        opened: false,
    };
}


export default {
    name: 'SideNav',
    props: {
        /**
         * Флаг закрытия по клику вне области панели
         */
        closeByClick: {
            type: Boolean,
            default: true,
        },
        /**
         * Флаг закрытия по нажатию на клавишу Escape
         */
        closeByEscape: {
            type: Boolean,
            default: true,
        },
        /**
         * Селектор кнопки закрытия
         */
        closeByButton: {
            type: String,
            default: '.close',
        },
        /**
         * Позиционирование по горизонтали (left, right, число пикселей, функция вычисления отступа)
         */
        positionX: {
            type: [String, Number, Function],
            default: 'right',
        },
        /**
         * Предикат проверки возможности закрытия панели
         */
        checkClose: {
            type: Function,
            default: (sidenav, data) => true,
        },
        /**
         * Всегда показывать кнопку "назад"
         */
        alwaysShowBack: {
            type: Boolean,
            default: false,
        },
        /**
         * Предикат проверки возможности закрытии страницы
         */
        checkPageClose: {
            type: Function,
            default: () => true,
        },
    },
    data() {
        return {
            confirm: emptyConfirm(),
        };
    },
    computed: {
        showBack() {
            if (this.alwaysShowBack)
                return true;

            for (let parent = this.$parent; parent != null; parent = parent.$parent) {
                if (parent.$options._componentTag === 'SideNav')
                    return true;
            }

            return false;
        },
    },
    methods: {
        open() {
            this.checkAuth();

            this.$options.proxy = newPromiseProxy();
            this.$modal.open();

            window.addEventListener('beforeunload', this.checkPageClose);

            return this.$options.proxy.promise;
        },
        accept(data) {
            this.$modal.close(data, { ignoreConfirm: true });
        },
        cancel() {
            this.$modal.close();
        },
        showConfirm(opts) {
            if (opts != null) {
                if (typeof (opts) !== 'object')
                    throw new TypeError('Parameter is not an object');
                Object.assign(this.confirm, emptyConfirm(), opts);
            } else {
                Object.assign(this.confirm, emptyConfirm());
            }

            this.confirm.opened = true;

            this.$options.confirmPromise = newPromiseProxy();
            return this.$options.confirmPromise.promise;
        },
        resolveConfirm(value) {
            this.confirm.opened = false;
            if (this.$options.confirmPromise)
                this.$options.confirmPromise.resolve(value);
        },
        acceptConfirm() {
            this.resolveConfirm(true);
        },
        cancelConfirm() {
            this.resolveConfirm(false);
        },
        async checkAuth() {
            const user = await getActual();

            // сайт на тех обслуживании
            if (user.underMaintenance) {
                this.$router.push({
                    name: 'tech-works',
                    query: { redirect: this.$route.fullPath },
                });
                return;
            }

            // пользователь не авторизован
            if (!user.id) {
                this.$router.push({
                    name: 'login',
                    query: { redirect: this.$route.fullPath, noAuth: true },
                });
            }
        },
    },
    mounted() {
        this.$modal = new Popup({
            container,
            contentClass: '',
            closeByClick: this.closeByClick,
            closeByEscape: this.closeByEscape,
            closeByButton: this.closeByButton,
            positionX: this.positionX,
            positionY: 'top',
            checkClose: (modal, data, opts) => {
                if (opts?.ignoreConfirm)
                    return true;

                return this.checkClose(this, data);
            },
            whenClosed: (modal, data) => {
                this.$emit('close');
                window.removeEventListener('beforeunload', this.checkPageClose);
                this.$options.proxy.resolve(data);
            },
        }, this.$el);
    },
    beforeDestroy() {
        if (this.$modal != null)
            this.$modal.remove();

        window.removeEventListener('beforeunload', this.checkPageClose);
    },
};
</script>

<style lang="scss">
@import '~@dsee/mixins.scss';

.side-nav-popup {
    width: 682px;
    height: 100vh;
    box-sizing: border-box;

    @media (max-device-width: 767px) {
        width: 100vw !important;
    }
}

.side-nav-inner {
    --main-background-color: #FFFFFF;
    --adhoc-background-color: rgba(6, 18, 51, 0.6);

    display: flex;
    flex-flow: column nowrap;
    overflow: hidden;
    background: transparent;
    height: 100%;
    position: relative;
    border-top-right-radius: 0;


    & > .header {
        flex: none;
        font-size: 18px;
        line-height: 24px;
        font-weight: bold;
        padding: 0;

        box-sizing: border-box;

        display: flex;
        align-items: center;
        background: transparent;

        .corner {
            flex: auto;
            background: var(--main-background-color);
            border-top-right-radius: 26px;
            padding: 20px 32px;

            .corner-inner {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
            }
        }

        .adhoc {
            max-width: 50%;
            height: 100%;
            width: 52px;
            background: var(--main-background-color);

            .adhoc-wrapper {
                background: var(--adhoc-background-color);
                width: 100%;
                height: 100%;
                border-bottom-left-radius: 30px;

                .adhoc-content {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    text-align: right;
                }
            }
        }


        & .back {
            @include icon(20px, 20px);
            background-image: url('./icons/arrow_left.svg');
            margin-right: 8px;
            opacity: 0.7;
            display: none;
        }

        & .title {
            flex: auto;
        }

        & .close {
            @include icon(24px, 24px);
            background-image: url('./icons/close.svg');
            color: #fff;
            margin: 8px;
        }
    }


    &.showBack {
        & > .header {
            .back {
                display: inline-block;
            }
        }
    }

    @media (max-device-width: 767px) {
        & > * {
            padding-right: 16px !important;
            padding-left: 16px !important;
        }
    }

    & > .header-sentinel {
        padding-bottom: 8px;
        background: #FFFFFF;
        border-top-right-radius: 12px;
    }
    
    & > .middle {
        flex: auto;
        overflow-x: hidden;
        overflow-y: auto;
        padding: 0 32px;
        @include scrollbar;
        background: var(--main-background-color);
        border-top-right-radius: 12px;
    }

    & > .footer {
        flex: none;
        padding: 20px 32px;
        display: flex;
        justify-content: end;
        box-shadow: none;

        & > * + * {
            margin-left: 8px;
        }

        & > * > input + input {
            margin-left: 8px;
        }

        &.two-sided > * > * + * {
            margin-left: 8px;
        }
    }

    .confirm {
        position: absolute;
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
        z-index: 2;
        background: #fff;
        box-sizing: border-box;
        border-radius: 32px;

        .container {
            display: flex;
            justify-content: center;
            flex-flow: column nowrap;
            padding: 20px 32px;
            text-align: left;
            max-width: 378px;
            overflow-wrap: anywhere;
        }

        .title {
            font-weight: 700;
            font-size: 18px;
            line-height: 24px;
        }

        .message {
            margin-top: 20px;
            margin-bottom: 0;

            &:empty {
                display: none;
            }
        }

        .buttons {
            margin-top: 20px;
            display: flex;
            flex-flow: row wrap;
            gap: 8px;
        }
    }
}
</style>
